import {
  getModalManager,
  type ModalShowConfig,
} from "@UI/ModalManager/index.js";
import {
  createList,
  type ListAPI,
  type ListItemProps,
} from "@UI/List/index.js";
import type { ProjectSummary, ProjectType } from "@/types/project-types";
import "./ProjectsBrowser.scss"; // co-located styles [[memory:8094583]]

export interface ProjectsBrowserConfig {
  readonly projects: readonly ProjectSummary[];
  readonly selectedProjectId?: string | null;
  readonly anchor?: ModalShowConfig["anchor"];
  readonly width?: number | string;
  readonly offset?: { x: number; y: number };
  readonly onSelect?: (projectId: string) => void;
  readonly onAddNew?: () => void;
  readonly onSearchChange?: (query: string) => void;
}

export interface ProjectsBrowserAPI {
  readonly getElement: () => HTMLElement;
  readonly show: () => void;
  readonly hide: () => void;
  readonly destroy: () => void;
}

const iconForProjectType = (type: ProjectType): string => {
  switch (type) {
    case "website":
      return "project_web";
    case "dashboard":
      return "project_dashboard";
    case "app":
    default:
      return "project_app";
  }
};

const labelForProject = (project: ProjectSummary): string => {
  const typeLabel =
    project.projectType.charAt(0).toUpperCase() +
    project.projectType.slice(1);
  try {
    const updated = project.updatedAt.toLocaleDateString();
    return `${typeLabel} • Updated ${updated}`;
  } catch {
    return typeLabel;
  }
};

export const createProjectsBrowser = (
  config: ProjectsBrowserConfig
): ProjectsBrowserAPI => {
  const {
    projects,
    selectedProjectId = null,
    width = 510,
    anchor = "bottom-center",
    offset = { x: 0, y: 100 },
    onSelect,
    onAddNew,
    onSearchChange,
  } = config;

  const modal = getModalManager();

  const container = document.createElement("div");
  container.className = "projects-browser";

  const list: ListAPI = createList<ProjectSummary>({
    items: projects,
    selectedId: selectedProjectId,
    header: {
      searchable: true,
      ...(onSearchChange && { onSearchChange }),
      ...(onAddNew && { onAddNew }),
    },
    mapItem: (p): ListItemProps => ({
      id: p.id,
      leftIcon: iconForProjectType(p.projectType),
      primaryLabel: p.name,
      secondaryLabel: labelForProject(p),
    }),
    onSelect: (id) => {
      onSelect?.(id);
      modal.hide();
    },
  });

  container.appendChild(list.getElement());

  const show = (): void => {
    const cfg: ModalShowConfig = {
      content: container,
      anchor,
      offset,
      width,
      closeOnBackdrop: true,
    };
    modal.show(cfg);
    // Focus first item on show
    requestAnimationFrame(() => list.focusFirst());
  };

  const hide = (): void => {
    if (modal.getCurrentContent() === container) modal.hide();
  };

  const destroy = (): void => {
    hide();
    list.destroy();
    container.remove();
  };

  return { getElement: () => container, show, hide, destroy };
};
